/* eslint-disable arrow-body-style */
import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";

import {Difference} from "../models/difference";
import {JournalStore} from "./journal-store-mobx";
import {Contractor, ContractorProps} from "../models/contractor";

export interface ContractorStoreProps {
    layerConfig: ApiLayerConfiguration;
    journalStore: JournalStore;
}

export class ContractorStore extends AbstractStore {

    journalStore: JournalStore | undefined;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, Contractor>();

    constructor(props: ContractorStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.journalStore = props.journalStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): Contractor[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });

        const searchTermLower = this.searchTerm?.toLowerCase().trim();

        let searchedContractors: Contractor[] | undefined;
        if (searchTermLower && searchTermLower.length > 0) {
            searchedContractors = filtered.filter((c) => {
                let idMatch: boolean = false;
                if (c.id && this.searchTerm && !isNaN(+this.searchTerm)) {
                    idMatch = c.id.toString().toLowerCase().includes(this.searchTerm);
                }
                const nameMatch = c.displayName?.toLowerCase().includes(searchTermLower);
                return idMatch || nameMatch;
            });
        }
        else {
            // no search filter
            searchedContractors = filtered;
        }

        // sort
        searchedContractors.sort(Contractor.sortByName);
        return [ ...searchedContractors];
    }

    /**
     * Override to use contractor id as key
     * Add one or multiple items to the store
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: Contractor | Map<string, Contractor>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(String(item.id))) {
                this.items.delete(String(item.id));
            }
            this.items.set(String(item.id), item);
        }
    }

    /**
     * Override to use contractor id as key
     * Remove an item from store
     * @param item
     */
    removeItem(item: Contractor): void {
        if (this.items.has(String(item.id))) {
            this.items.delete(String(item.id));
        }
    }

    /**
     * Override to use contractor id as key
     * Get an item by GUID
     * @param contractorId
     */
    getItem(contractorId: string): Contractor | null {
        const item = this.items.get(contractorId);
        return item || null;
    }

    loadItem(contractorId: number | undefined): Promise<Contractor | null> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!contractorId) {
                    reject();
                    return;
                }

                this.queryFeatures([contractorId]).then((contractors: Contractor[]) => {
                    if (contractors[0] && contractors[0].id === contractorId) {
                        const contractor = contractors[0];
                        const loadedContractor = this.items.get(contractor.guid);
                        if (loadedContractor) {
                            // return contractor as long as merging contractor is running. contractor can be displayed with
                            // indicator that synching is in contractor
                            // TODO: merge contractor with already loaded contractor
                        }
                        this.addItem(contractor);
                        this.setSelectedItem(contractor);
                        this.setDataStatus("loaded");
                        resolve(contractor);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Contractor not found");
                    }
                });
            });
        });
    }

    /**
     * Request contractors from server and store in local store
     * @param contractorIds (Optional) IDs of contractors
     */
    loadItems(contractorIds?: number[]): Promise<Contractor[]> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available
                this.queryFeatures(contractorIds).then((contractors: Contractor[]) => {
                    if (contractors) {
                        const map = new Map<string, Contractor>();
                        contractors.forEach((p: Contractor) => {
                            map.set(String(p.id), p);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(contractors);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting contractors");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param user
     */
    newItem(user: string): Promise<Contractor | null> {
        return new Promise((resolve) => {
            const randomIds = new Uint16Array(1);
            self.crypto.getRandomValues(randomIds);

            const contractor = new Contractor({
                id: randomIds[0],
                guid: crypto.randomUUID(),
                creator: user
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                contractor.feature = this.createFeature(null, contractor);

                // workaround: add missing prototype attributes
                Contractor.prototypeAttributes.forEach((attrName) => {
                    if (!contractor.feature.attributes[attrName]) {
                        contractor.feature.attributes[attrName] = null;
                    }
                });

                this.addItem(contractor);
                this.setSelectedItem(contractor);

                resolve(contractor);
            });

        });
    }

    /**
     * Save contractor to the server
     *
     * @param contractor
     * @param updated
     * @param user
     */
    save(contractor: Contractor | null,
         updated: any,
         user: string): Promise<Contractor | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!contractor?.guid) {
                    reject("Contractor undefined when saving edits.");
                    return;
                }

                if (contractor.isNewRecord) {
                    // New contractor --> create feature
                    contractor.feature = this.createFeature(null, contractor);

                    // log project creation in journal
                    const nameDiff = new Difference({p: "organisation", n: updated["organisation"] || contractor.organisation});
                    const idDiff = new Difference({p: "guid", n: contractor.guid.toString()});
                    this.journalStore?.journalizeItem(
                        contractor,
                        "contractor",
                        "add",
                        user,
                        [idDiff, nameDiff],
                        "Neuer Kontakt"
                    );
                }

                if(!contractor.isNewRecord) {
                    // log changes to journal
                    const diff = this.compareAttributes(contractor, updated, Contractor.comparableAttributes);
                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        contractor,
                        "contractor",
                        "update",
                        user,
                        diff,
                        "Kontakt geändert");
                }

                const feature = contractor?.feature;
                if (!feature) {
                    reject(`Feature undefined for contractor ${contractor.guid}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = Contractor.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            reject();
                            throw new Error(`Database field not found for contractor ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = contractor.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(contractor, attrs);
                    if (contractor.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        contractor.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(contractor);
                }, (error) => {
                    console.error("Error saving contractor", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete an contractor
     *
     * @param contractor
     * @param user
     */
    delete(contractor: Contractor | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!contractor?.guid) {
                    reject("Contractor GUID undefined when saving edits.");
                    return;
                }

                if (contractor.isNewRecord) {
                    reject("This contractor is not saved. Does not have to be deleted.");
                    return;
                }

                // TODO: do not allow to delete when related to activities

                const feature = contractor?.feature;
                if (!feature) {
                    reject(`Feature undefined for contractor ${contractor.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {
                    this.resetSelectedItem();
                    this.removeItem(contractor);

                    // log deletion in journal
                    const nameDiff = new Difference({p: "organisation", o: contractor.organisation});
                    const idDiff = new Difference({p: "guid", o: contractor.guid});
                    this.journalStore?.journalizeItem(
                        contractor,
                        "contractor",
                        "delete",
                        user,
                        [idDiff, nameDiff],
                        "Kontakt gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(contractorIds?: number[]): Promise<Contractor[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                // load all features if no project ID is specified
                if (contractorIds !== undefined) {
                    // Add to the layer's current definitionExpression
                    // remove undefined or null values
                    const validIds = contractorIds.filter((id) => id !== null && id !== undefined);
                    if (validIds.length > 0) {
                        queryParams.where = queryParams.where + ` AND ID IN (${validIds.join(",")})`;
                    }
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const contractors = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(contractors);
                    }
                    reject();
                }, (error) => {
                    throw new Error(`Error querying contractors + ${error}`);
                });
            });
        });
    }

    maxId(start: number): Promise<number | null> {
        return new Promise((resolve) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                const queryParams = this.layer.createQuery();
                queryParams.returnGeometry = false;
                queryParams.where += ` AND CAST(ID as VARCHAR(20)) LIKE '${start}%'`;

                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const contractors = this.parseQueryResult(results);
                        if (contractors) {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            const ids: number[] = contractors.map((p) => p.id).filter((id) => {
                                return id !== null && id !== undefined;
                            });
                            if (!ids || ids.length === 0) {
                                resolve(null);
                                return;
                            }
                            const maxId = Math.max(...ids);
                            resolve(maxId);
                            return;
                        }
                    }
                    resolve(null);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): Contractor[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const contractors: Contractor[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, Contractor);
            const contractor = new Contractor(json as ContractorProps);
            contractor.feature = feature;

            contractors.push(contractor);
        });
        return contractors;
    }

}
